import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Navigate } from 'react-router-dom';
import { Camera, Trash2, Lock, AlertTriangle, Info } from 'lucide-react';
import axios from 'axios';
import profileService from '../services/profileService';
import { isJobSeekerProfileComplete } from '../components/Auth/OnboardingGate';
import { LITHUANIAN_CITIES, LITHUANIAN_MUNICIPALITIES, pinMajorCityNamesFirst } from '../constants/cities';
import './EmployeeProfile.scss';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const MONTHS = [
  'Sausis', 'Vasaris', 'Kovas', 'Balandis', 'Gegužė', 'Birželis',
  'Liepa', 'Rugpjūtis', 'Rugsėjis', 'Spalis', 'Lapkritis', 'Gruodis'
];

interface EmployeeProfileData {
  first_name: string;
  last_name: string;
  phone: string;
  birth_date: string;
  location: string;
  gender: 'male' | 'female' | '';
}


const EmployeeProfile: React.FC = () => {
  const { user, isAuthenticated, isLoading, refreshProfile } = useAuth();
  const [formData, setFormData] = useState<EmployeeProfileData>({
    first_name: '',
    last_name: '',
    phone: '',
    birth_date: '',
    location: '',
    gender: ''
  });

  const initialFormData = useRef<EmployeeProfileData | null>(null);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);
  const [passwordResetSending, setPasswordResetSending] = useState(false);
  const { showToast } = useToast();

  const handleChangePassword = async () => {
    if (!user?.email || passwordResetSending) return;
    setPasswordResetSending(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email: user.email });
      showToast('Slaptažodžio keitimo nuoroda išsiųsta į jūsų el. paštą', 'success');
    } catch {
      showToast('Nepavyko išsiųsti. Bandykite dar kartą.', 'error');
    } finally {
      setPasswordResetSending(false);
    }
  };

  // Date picker state (day/month/year dropdowns)
  const [dateDay, setDateDay] = useState('');
  const [dateMonth, setDateMonth] = useState('');
  const [dateYear, setDateYear] = useState('');

  const currentYear = new Date().getFullYear();
  const years = useMemo(() => Array.from({ length: 80 }, (_, i) => currentYear - 16 - i), [currentYear]);
  const daysInMonth = useMemo(() => {
    if (!dateMonth || !dateYear) return 31;
    return new Date(parseInt(dateYear), parseInt(dateMonth), 0).getDate();
  }, [dateMonth, dateYear]);
  const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);

  const handleDatePartChange = (part: 'day' | 'month' | 'year', value: string) => {
    const newDay = part === 'day' ? value : dateDay;
    const newMonth = part === 'month' ? value : dateMonth;
    const newYear = part === 'year' ? value : dateYear;
    if (part === 'day') setDateDay(value);
    if (part === 'month') setDateMonth(value);
    if (part === 'year') setDateYear(value);
    if (newDay && newMonth && newYear) {
      const d = newDay.padStart(2, '0');
      const m = newMonth.padStart(2, '0');
      setFormData(prev => ({ ...prev, birth_date: `${newYear}-${m}-${d}` }));
    } else {
      setFormData(prev => ({ ...prev, birth_date: '' }));
    }
  };

  useEffect(() => {
    if (user) {
      const rawDate = user.profile?.birth_date || '';
      const data: EmployeeProfileData = {
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        birth_date: rawDate,
        location: user.profile?.location || '',
        gender: user.profile?.gender || ''
      };
      setFormData(data);
      initialFormData.current = { ...data };
      if (rawDate) {
        const d = new Date(rawDate);
        if (!isNaN(d.getTime())) {
          setDateDay(String(d.getUTCDate()));
          setDateMonth(String(d.getUTCMonth() + 1));
          setDateYear(String(d.getUTCFullYear()));
        }
      } else {
        setDateDay(''); setDateMonth(''); setDateYear('');
      }

      // Set current profile image
      if (user.profile?.profile_image) {
        const imageUrl = profileService.getProfileImageUrl(user.profile.profile_image);
        if (imageUrl) {
          setPreviewUrl(imageUrl);
        }
      }
    }
  }, [user]);

  const hasChanges = useMemo(() => {
    if (!initialFormData.current) return false;
    if (profileImage) return true;
    const init = initialFormData.current;
    return (Object.keys(init) as (keyof EmployeeProfileData)[]).some(
      key => formData[key] !== init[key]
    );
  }, [formData, profileImage]);

  const isGoogleUser = !!user?.google_id;

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="employee-profile-page">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  // Redirect if not authenticated or not an employee
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (user?.user_type !== 'employee' && user?.user_type !== 'job_seeker') {
    return <Navigate to="/" replace />;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteImage = async () => {
    try {
      setLoading(true);
      const result = await profileService.deleteProfileImage();
      if (result.success) {
        setPreviewUrl(null);
        setProfileImage(null);
        setSuccess('Profilio nuotrauka sėkmingai ištrinta');
        setShowDeleteConfirm(false);
        refreshProfile();
      } else {
        setError(result.message || 'Nepavyko ištrinti profilio nuotraukos');
      }
    } catch (err: any) {
      setError('Įvyko klaida trinant profilio nuotrauką');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const missing: string[] = [];
    if (!formData.birth_date) missing.push('Gimimo data');
    if (!formData.gender) missing.push('Lytis');
    if (!formData.location) missing.push('Gyvenamoji vieta');
    if (missing.length > 0) {
      const msg = `Užpildykite privalomus laukus: ${missing.join(', ')}`;
      setError(msg);
      showToast(msg, 'error');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await profileService.updateProfileWithImage(formData, profileImage || undefined);
      if (result.success) {
        setSuccess('Profilis sėkmingai atnaujintas!');
        setProfileImage(null);
        refreshProfile();
      } else {
        setError(result.message || 'Nepavyko atnaujinti profilio');
      }
    } catch (err: any) {
      setError('Įvyko klaida atnaujinant profilį');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      const rawDate = user.profile?.birth_date || '';
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        birth_date: rawDate,
        location: user.profile?.location || '',
        gender: user.profile?.gender || ''
      });
      if (rawDate) {
        const d = new Date(rawDate);
        if (!isNaN(d.getTime())) {
          setDateDay(String(d.getUTCDate()));
          setDateMonth(String(d.getUTCMonth() + 1));
          setDateYear(String(d.getUTCFullYear()));
        }
      } else {
        setDateDay(''); setDateMonth(''); setDateYear('');
      }
      setProfileImage(null);
      if (user.profile?.profile_image) {
        const imageUrl = profileService.getProfileImageUrl(user.profile.profile_image);
        if (imageUrl) {
          setPreviewUrl(imageUrl);
        }
      } else {
        setPreviewUrl(null);
      }
    }
  };

  return (
    <div className="employee-profile-page">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <span>Titulinis &gt; Profilis</span>
        </nav>

        {/* Page Header */}
        <div className="page-header">
          <h1>Profilis</h1>
          <div className="header-actions">
            <div className="password-btn-wrapper">
              <button 
                type="button"
                className="btn-change-password"
                onClick={handleChangePassword}
                disabled={passwordResetSending || isGoogleUser}
              >
                <Lock size={16} />
                {passwordResetSending ? 'Siunčiama...' : 'Pakeisti slaptažodį'}
              </button>
              {isGoogleUser && (
                <span className="google-password-hint" data-tooltip="Prisijungėte per Google paskyrą. Slaptažodžio keitimas negalimas. Susisiekite su mumis jei reikia pagalbos.">
                  <Info size={16} />
                </span>
              )}
            </div>
            <button 
              type="button"
              className="btn-delete-account"
              onClick={() => setShowDeleteAccountConfirm(true)}
            >
              <Trash2 size={16} />
              Pašalinti paskyrą
            </button>
          </div>
        </div>

        {/* Onboarding notice — explains why the rest of the app is locked */}
        {!isJobSeekerProfileComplete(user) && (
          <div className="alert alert-info">
            <Info size={16} />
            Prašome užpildyti profilį (vardą, pavardę ir gyvenamąją vietą), kad galėtumėte naudotis visomis sistemos funkcijomis.
          </div>
        )}

        {/* Messages */}
        {error && (
          <div className="alert alert-error">
            {error}
            <button className="alert-close" onClick={() => setError(null)}>×</button>
          </div>
        )}
        {success && (
          <div className="alert alert-success">
            {success}
            <button className="alert-close" onClick={() => setSuccess(null)}>×</button>
          </div>
        )}

        {/* Profile Form */}
        <div className="profile-container">
          <form onSubmit={handleSubmit}>
            <div className="profile-layout">
              {/* Left - Profile Photo */}
              <div className="profile-photo-section">
                <div className="photo-wrapper">
                  {previewUrl ? (
                    <div className="photo-preview">
                      <img src={previewUrl} alt="Profile" />
                      <button 
                        type="button"
                        className="delete-photo-btn"
                        onClick={() => setShowDeleteConfirm(true)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="photo-placeholder">
                      <Camera size={48} strokeWidth={1.5} />
                    </div>
                  )}
                </div>
                
                <label className="upload-photo-btn">
                  <Camera size={16} />
                  Įkelti nuotrauką
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                </label>
                
                <p className="upload-note">
                  Nuotrauka turi būti JPG, GIF, PNG formatu. Maksimalus dydis: 5 MB
                </p>
              </div>

              {/* Right - Form Fields */}
              <div className="profile-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="first_name">Vardas</label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="location">Gyvenamoji vieta *</label>
                    <select
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Pasirinkite miestą</option>
                      <optgroup label="Miestai">
                        {pinMajorCityNamesFirst(LITHUANIAN_CITIES).map(city => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                      </optgroup>
                      {LITHUANIAN_MUNICIPALITIES.map(group => (
                        <optgroup key={group.county} label={group.county}>
                          {group.municipalities.map(m => (
                            <option key={m} value={m}>{m}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="last_name">Pavardė</label>
                    <input
                      type="text"
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="phone">Telefono nr.</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+370 600 00000"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Gimimo data *</label>
                    <div className="date-picker-row">
                      <select
                        value={dateDay}
                        onChange={e => handleDatePartChange('day', e.target.value)}
                        className="date-select"
                      >
                        <option value="">DD</option>
                        {days.map(d => (
                          <option key={d} value={String(d)}>{String(d).padStart(2, '0')}</option>
                        ))}
                      </select>
                      <select
                        value={dateMonth}
                        onChange={e => handleDatePartChange('month', e.target.value)}
                        className="date-select"
                      >
                        <option value="">MM</option>
                        {MONTHS.map((m, i) => (
                          <option key={i + 1} value={String(i + 1)}>{m}</option>
                        ))}
                      </select>
                      <select
                        value={dateYear}
                        onChange={e => handleDatePartChange('year', e.target.value)}
                        className="date-select date-select-year"
                      >
                        <option value="">MMMM</option>
                        {years.map(y => (
                          <option key={y} value={String(y)}>{y}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">El. paštas</label>
                    <input
                      type="email"
                      id="email"
                      value={user?.email || ''}
                      disabled
                    />
                  </div>
                </div>

                <div className="form-group gender-group">
                  <label>Lytis: *</label>
                  <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="gender"
                        value="male"
                        checked={formData.gender === 'male'}
                        onChange={handleInputChange}
                      />
                      <span>Vyras</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="gender"
                        value="female"
                        checked={formData.gender === 'female'}
                        onChange={handleInputChange}
                      />
                      <span>Moteris</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="form-actions">
              <button 
                type="button"
                className="btn-cancel"
                onClick={handleCancel}
              >
                <span>✕</span>
                ATŠAUKTI
              </button>
              <button
                type="submit"
                className={`btn-submit ${!hasChanges && !loading ? 'inactive' : ''}`}
                disabled={loading || !hasChanges}
              >
                <span>✓</span>
                {loading ? 'IŠSAUGOMA...' : 'IŠSAUGOTI PAKEITIMUS'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Delete Photo Confirmation Modal */}
      {showDeleteConfirm && ReactDOM.createPortal(
        <div className="confirm-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">
              <AlertTriangle size={48} />
            </div>
            <h2>Ar tikrai norite ištrinti profilio nuotrauką?</h2>
            <p>Šis veiksmas negrįžtamas.</p>
            <div className="confirm-actions">
              <button className="btn-cancel-modal" onClick={() => setShowDeleteConfirm(false)}>
                Atšaukti
              </button>
              <button className="btn-confirm-delete" onClick={handleDeleteImage}>
                Taip, ištrinti
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteAccountConfirm && ReactDOM.createPortal(
        <div className="confirm-modal-overlay" onClick={() => setShowDeleteAccountConfirm(false)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">
              <AlertTriangle size={48} />
            </div>
            <h2>Ar tikrai norite ištrinti savo paskyrą?</h2>
            <p>Šis veiksmas yra negrįžtamas. Visi jūsų duomenys bus pašalinti visam laikui.</p>
            <div className="confirm-actions">
              <button className="btn-cancel-modal" onClick={() => setShowDeleteAccountConfirm(false)}>
                Atšaukti
              </button>
              <button className="btn-confirm-delete" onClick={() => showToast('Delete account - coming soon', 'info')}>
                OK
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default EmployeeProfile;
